import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
    Target,
    AlertCircle,
    Users,
    BookOpen,
    Zap,
    Globe,
    TrendingUp
} from 'lucide-react';
// InvokeLLM removed - no longer using Base44 SDK
import { useTranslation } from '@/components/utils/translations';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant, isLibraryAccountPageTenant } from '@/lib/tenantUtils';

// Helper function to clean and format text items - removes JSON array markers and escapes
const cleanTextItem = (item) => {
    if (!item) return '';
    
    let text = String(item);
    
    // Remove JSON array markers like [" and "]
    text = text.replace(/^\[\"/g, '').replace(/\"\]$/g, '').replace(/^\[/g, '').replace(/\]$/g, '');
    text = text.replace(/^\"/g, '').replace(/\"$/g, '');
    
    // Remove any remaining array-like formatting
    text = text.replace(/^\{"/g, '').replace(/"\}$/g, '');
    
    // Unescape escaped quotes
    text = text.replace(/\\"/g, '"').replace(/\\'/g, "'");
    
    // Trim whitespace
    text = text.trim();
    
    return text;
};

function coerceStrategyPoints(raw) {
    if (raw == null || raw === '') return [];
    if (Array.isArray(raw)) return raw.flatMap(coerceStrategyPoints);
    if (typeof raw === 'object') {
        const nested = raw.items ?? raw.points ?? raw.challenges ?? raw.bullets ?? raw.values;
        if (nested != null) return coerceStrategyPoints(nested);
        if (raw.text != null) return coerceStrategyPoints(raw.text);
        if (raw.summary != null) return coerceStrategyPoints(raw.summary);
        if (raw.body != null) return coerceStrategyPoints(raw.body);
        return Object.values(raw).flatMap(coerceStrategyPoints);
    }
    const text = String(raw).trim();
    if (!text) return [];
    if ((text.startsWith('[') || text.startsWith('{')) && (text.endsWith(']') || text.endsWith('}'))) {
        try {
            return coerceStrategyPoints(JSON.parse(text));
        } catch {
            /* fall through */
        }
    }
    return text
        .split(/\n+/)
        .map((line) => line.replace(/^[-•*]\s*/, '').trim())
        .filter((line) => line.length > 0);
}

function institutionalChallengesFromAccount(account) {
    const keys = [
        'institutional_challenges',
        'institution_challenges',
        'institutionalChallenges',
        'challenges',
    ];
    for (const key of keys) {
        const points = coerceStrategyPoints(account?.[key]);
        if (points.length) return points;
    }
    return [];
}

function hasRenderableStrategyText(points) {
    if (!Array.isArray(points) || points.length === 0) return false;
    const text = points.map((p) => String(p || '').trim()).join('');
    if (!text) return false;
    if (/^[-–—_.\s]+$/.test(text)) return false;
    return text.length >= 3;
}

/** Institutional Challenges belongs on Overview for every tenant; insert after Strategic Priorities. */
function withInstitutionalChallenges(keys) {
    if (!Array.isArray(keys) || keys.includes('institutional_challenges')) return keys;
    const i = keys.indexOf('strategic_priorities');
    if (i === -1) return ['institutional_challenges', ...keys];
    return [...keys.slice(0, i + 1), 'institutional_challenges', ...keys.slice(i + 1)];
}

const StrategyCard = ({ icon, title, color, points, t }) => {
    // Clean and format points — split newline-separated fact lines into one bullet each
    const cleanedPoints =
      points
        ?.map(cleanTextItem)
        .flatMap((item) =>
          item
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line.length > 0),
        ) || [];
    
    return (
        <Card className="border-0 shadow-none">
            <CardHeader className="flex flex-row items-center gap-2 pb-2">
                {React.cloneElement(icon, { className: `w-4 h-4 ${color}` })}
                <CardTitle className="text-base font-semibold">{title}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                {(!cleanedPoints || cleanedPoints.length === 0) ? (
                    <p className="text-sm text-gray-500">{t('noDataAvailable') || 'No data available.'}</p>
                ) : (
                    <ul className="list-disc list-outside ml-5 mr-2 text-sm text-[#5C6B7A] space-y-2">
                        {cleanedPoints.map((item, index) => (
                            <li key={index} className="break-words whitespace-pre-wrap leading-relaxed">
                                {item}
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>
        </Card>
    );
};


export default function StrategyCards({ account }) {
    const { t } = useTranslation();
    const contextUser = useUser();
    const currentTenant = useCurrentTenant();
    // Removed insights state - no longer using LLM fallback

    // Get effective tenant - admin-gated: only admins may use admin-active-tenant
    const adminTenant =
      contextUser?.role === 'admin'
        ? localStorage.getItem('admin-active-tenant')
        : null;
    const effectiveTenant = resolveEffectiveTenant(
      adminTenant,
      currentTenant,
      contextUser,
      account?.tenant_id || 'instructure'
    );

// Debug for Calmedica
if (effectiveTenant === 'calmedica' || account?.tenant_id === 'calmedica') {
    console.log('📊 StrategyCards [calmedica] raw data:', {
        strategic_priorities: account?.strategic_priorities,
        strategic_priorities_type: typeof account?.strategic_priorities,
        strategic_priorities_length: account?.strategic_priorities?.length,
        patient_gaps: account?.patient_gaps,
        patient_gaps_type: typeof account?.patient_gaps,
        effectiveTenant,
        accountTenantId: account?.tenant_id
    });
}

    const isDemarque = isLibraryAccountPageTenant(effectiveTenant);
    const isEdqwest = effectiveTenant === 'edqwest';
    const isFaria = effectiveTenant === 'faria';
    const isCalmedica = effectiveTenant === 'calmedica';
    const isProwise = effectiveTenant === 'prowise';
    const isSkillwell = effectiveTenant === 'skillwell';
    const isKairadigital = effectiveTenant === 'kairadigital';
    const isWayground = effectiveTenant === 'wayground';
    const isPlataformaa = effectiveTenant === 'plataformaa';
    const isCayuse = effectiveTenant === 'cayuse' || account?.tenant_id === 'cayuse';
    const isInfonetica = effectiveTenant === 'infonetica' || account?.tenant_id === 'infonetica';
    const isSeba = effectiveTenant === 'seba' || account?.tenant_id === 'seba';

    // LLM fallback removed - no longer using Base44 SDK
    // Cards will simply show "No data available" if account data is missing

    // Define all possible sections with their configurations
    const allSections = {
        strategic_priorities: {
            key: 'strategic_priorities',
            title: t('strategicPriorities'),
            icon: <Target />,
            color: "text-[#00C2C2]",
        },
        digital_funding: {
            key: 'digital_funding',
            title: t('digitalFunding') || 'Digital Funding Opportunities',
            icon: <TrendingUp />,
            color: "text-green-500",
        },
        institutional_challenges: {
            key: 'institutional_challenges',
            title: isSeba ? t('financialSituation') : t('institutionalChallenges'),
            icon: <AlertCircle />,
            color: "text-orange-500",
        },
        digital_landscape: {
            key: 'digital_landscape',
            title: t('digitalLandscape') || 'Digital Landscape',
            icon: <Globe />,
            color: "text-orange-500",
        },
        pedagogy: {
            key: 'pedagogy',
            title: t('pedagogicalModel') || 'Pedagogical & Cultural Model',
            icon: <BookOpen />,
            color: "text-purple-500",
        },
        student_expectations: {
            key: 'student_expectations',
            title: isSeba ? t('marketConditions') : t('studentExpectations'),
            icon: <Users />,
            color: "text-blue-500",
        },
        patient_gaps: {
            key: 'patient_gaps',
            title: t('patientCareGaps') || 'Patient Care Gaps',
            icon: <AlertCircle />,
            color: "text-red-500",
        },
        learning_initiatives: {
            key: 'pedagogy',
            title: t('learningInitiatives') || 'Learning Development Initiatives',
            icon: <BookOpen />,
            color: "text-purple-500",
        },
        decision_dynamics: {
            key: 'decision_dynamics',
            title: t('decisionDynamics') || 'Stakeholder Decision Dynamics',
            icon: <Users />,
            color: "text-purple-500",
        },
        content_coverage: {
            key: 'content_coverage',
            title: t('contentCoverage') || 'Content Coverage',
            icon: <Users />,
            color: "text-blue-500",
        },
        client_opportunities: {
            key: 'client_opportunities',
            title: t('clientOpportunities') || 'Client Opportunities',
            icon: <TrendingUp />,
            color: "text-green-500",
        },
        decision_makers: {
            key: 'decision_makers',
            title: t('decisionMakers') || 'Decision Makers',
            icon: <Users />,
            color: "text-purple-500",
        },
        hooks: {
            key: 'hooks',
            title: t('engagementHooks') || 'Engagement Hooks',
            icon: <Zap />,
            color: "text-yellow-500",
        },
        tactics: {
            key: 'tactics',
            title: t('recommendedTactics') || 'Recommended Tactics',
            icon: <Target />,
            color: "text-indigo-500",
        }
    };

    // Tenant-specific section ordering. Institutional Challenges is always inserted
    // after Strategic Priorities — it was previously omitted for several brands.
    const getSectionsForTenant = () => {
        if (isDemarque) {
            return ['strategic_priorities', 'digital_landscape', 'digital_funding', 'student_expectations',
                    'content_coverage', 'client_opportunities', 'decision_makers', 'hooks', 'tactics'];
        } else if (isEdqwest) {
            return ['strategic_priorities', 'digital_landscape', 'pedagogy'];
        } else if (isFaria) {
            return ['strategic_priorities', 'digital_landscape', 'pedagogy'];
        } else if (account?.tenant_id === 'sebit' || account?.tenant_id === 'Sebit') {
            return ['strategic_priorities'];
        } else if (isCalmedica) {
            return ['strategic_priorities', 'patient_gaps', 'institutional_challenges'];
        } else if (isProwise) {
            return ['strategic_priorities', 'pedagogy', 'digital_landscape'];
        } else if (isSkillwell) {
            return ['strategic_priorities', 'institutional_challenges', 'learning_initiatives'];
        } else if (isKairadigital) {
            return ['strategic_priorities', 'institutional_challenges', 'decision_dynamics'];
        } else if (isWayground) {
            return ['strategic_priorities', 'digital_landscape', 'pedagogy'];
        } else if (isPlataformaa) {
            return ['strategic_priorities', 'pedagogy', 'digital_landscape', 'digital_funding'];
        } else if (isCayuse || isInfonetica) {
            return ['strategic_priorities', 'patient_gaps', 'digital_funding', 'institutional_challenges', 'student_expectations'];
        } else {
            return ['strategic_priorities', 'digital_funding', 'institutional_challenges', 'student_expectations'];
        }
    };

    const sectionOrder = withInstitutionalChallenges(getSectionsForTenant());
    const sectionsDefinition = sectionOrder.map(sectionKey => allSections[sectionKey]).filter(Boolean);

    const cardsToRender = sectionsDefinition
        .map(section => {
            let points = section.key === 'institutional_challenges'
                ? institutionalChallengesFromAccount(account)
                : account?.[section.key];
        
            // LLM fallback removed - only use account data
        
            // Ensure points is always an array
if (typeof points === 'string') {
    // Try to parse as JSON if it looks like JSON
    try {
        const parsed = JSON.parse(points);
        points = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
        // Not JSON, treat as single string item
        points = points.trim() ? [points] : [];
    }
} else if (!Array.isArray(points)) {
    // Handle case where points is an object or other type
    points = points ? [String(points)] : [];
}

// Clean each point of JSON artifacts
points = points.map(p => {
    if (typeof p === 'string') {
        return p.replace(/^\[?"?|"?\]?$/g, '').trim();
    }
    return String(p);
}).filter(p => p.length > 0);

            // Debug logging for specific tenants
            if (account?.tenant_id === 'faria' && (section.key === 'digital_landscape' || section.key === 'pedagogy')) {
                console.log(`📊 StrategyCards [${account?.tenant_id}]: ${section.title}`, {
                    key: section.key,
                    rawData: account?.[section.key],
                    rawDataType: typeof account?.[section.key],
                    rawDataIsArray: Array.isArray(account?.[section.key]),
                    points: points,
                    pointsLength: points?.length,
                    hasData: points && points.length > 0,
                    pointsType: typeof points,
                    isArray: Array.isArray(points),
                    sectionInOrder: sectionOrder.includes(section.key)
                });
            }
            if (account?.tenant_id === 'plataformaa' && section.key === 'digital_landscape') {
                console.log(`📊 StrategyCards [${account?.tenant_id}]: ${section.title}`, {
                    key: section.key,
                    rawData: account?.[section.key],
                    points: points,
                    pointsLength: points?.length,
                    hasData: points && points.length > 0,
                    pointsType: typeof points,
                    isArray: Array.isArray(points)
                });
            }
            if (account?.tenant_id === 'kairadigital' && section.key === 'institutional_challenges') {
                console.log(`📊 StrategyCards [${account?.tenant_id}]: ${section.title}`, {
                    key: section.key,
                    rawData: account?.[section.key],
                    points: points,
                    hasData: points && points.length > 0
                });
            }

            return {
                ...section,
                points: points,
            };
        })
        .filter((card) => {
            if (!card.points || card.points.length === 0) return false;
            if (card.key === 'institutional_challenges' && !hasRenderableStrategyText(card.points)) {
                return false;
            }
            return true;
        });

    console.log('📊 Final cards to render:', cardsToRender.map(c => ({ title: c.title, pointCount: c.points?.length })));

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {cardsToRender.map((card, index) => (
                <StrategyCard
                    key={index}
                    icon={card.icon}
                    title={card.title}
                    color={card.color}
                    points={card.points}
                    t={t}
                />
            ))}
        </div>
    );
}